import {
	type DiscountedPrice,
	type LineItem,
} from '@commercetools/platform-sdk'
import {
	Link,
	useFetcher,
	useLoaderData,
	useLocation,
	useNavigation,
	useSearchParams,
} from '@remix-run/react'
import { WheelGesturesPlugin } from 'embla-carousel-wheel-gestures'
import { type TFunction } from 'i18next'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import ImageComponent from '#app/components/common/image-component'
import PlentyBody from '#app/components/typography/plenty-body'
import { Button } from '#app/components/ui/button'

import {
	Carousel,
	CarouselContent,
	CarouselItem,
} from '#app/components/ui/carousel'
import { Icon } from '#app/components/ui/icon'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '#app/components/ui/select'
import { LoadingSpinner } from '#app/components/ui/spinner'
import { useFetcherWithReset } from '#app/hooks/useFetcherWithReset'
import {
	type loader,
	type action,
} from '#app/routes/_public+/product+/$styleNumber'
import { useCartStore } from '#app/store/cart'
import { useClerkVisitorStore } from '#app/store/clerk-visitor'
import { cn } from '#app/utils/misc'
import { moneyFormatWithTypedMoney } from '#app/utils/money'
import { trackProductAdded } from '#app/utils/tracking/amplitude/product-added'
import { type PlentyVariantAvailability } from '#types/product'
import { getSizeOptions } from '../utils'
import SizeGuideDialog from './actions/size-guide-dialog'
import NewSizeSelector, { type SizeOption } from './size-selector-new'

interface ProductActionsProps {
	selectedSku: string | null | undefined
	setSelectedSku: (val: string) => void
	soldOut: boolean
	completelySoldOut: boolean
	firstAvailability: PlentyVariantAvailability[]
	selectSizeOpen: boolean
	setSelectSizeOpen: React.Dispatch<React.SetStateAction<boolean>>
}

const ProductActions = ({
	selectedSku = '',
	setSelectedSku,
	soldOut,
	completelySoldOut,
	firstAvailability,
	selectSizeOpen,
	setSelectSizeOpen,
}: ProductActionsProps) => {
	const metaPixelFetcher = useFetcher()
	const [searchParams, setSearchParams] = useSearchParams()
	const loaderData = useLoaderData<typeof loader>()
	const productData = loaderData?.productData
	const location = useLocation()
	const amplitudePageSource = location.state?.amplitudePageSource
	const productFetcher = useFetcherWithReset<typeof action>({ key: 'addToBag' })
	const brandKey = productData?.productInfo.masterVariant.attributes?.find(
		item => item.name === 'brand',
	)?.value
	const { cart } = useCartStore()
	let { t } = useTranslation('product_details_page')
	const { clerkVisitor } = useClerkVisitorStore()
	const activeStyle =
		productData?.styleAvailableVariants.find(item => item.active) ||
		productData!.styleAvailableVariants[0]

	const getQuantity = (storeKey?: string) => {
		const selectedVariant = activeStyle.variants.find(
			variant => variant.sku === selectedSku,
		)
		if (!selectedVariant || !cart) {
			return 0
		}
		const channelId = selectedVariant.availability?.find(
			it => it.storeKey === storeKey,
		)?.channelKey
		return cart.lineItems.reduce((acc: number, item: LineItem) => {
			// Check both SKU match and, if a storeKey is provided, channel match.
			const isCorrectVariant = item.variant.sku === selectedVariant.sku
			const isCorrectChannel = storeKey
				? item.distributionChannel?.id === channelId
				: true
			if (isCorrectVariant && isCorrectChannel) {
				return acc + item.quantity
			}
			return acc
		}, 0)
	}

	const getVariantAvailability = (storeKey?: string) => {
		const selectedVariant = activeStyle.variants.find(
			variant => variant.sku === selectedSku,
		)

		if (!selectedVariant) {
			return 0
		}

		if (storeKey) {
			const availability = selectedVariant.availability?.find(
				availability => availability.storeKey === storeKey,
			)
			return availability?.availableQuantity || 0
		}

		return (
			selectedVariant.availability?.reduce((total, availability) => {
				const quantity = (availability.availableQuantity as number) || 0
				return total + quantity
			}, 0) || 0
		)
	}
	const handleAddToBag = () => {
		if (selectedSku) {
			// check the quantity on the cart vs the available quantity
			const selectedAvailability = activeStyle.variants
				.find(variant => variant.sku === selectedSku)
				?.availability?.find(
					it =>
						(it.availableQuantity as number) > 0 &&
						loaderData?.storeParams.includes(it.storeKey as string),
				)

			const cartQuantity = getQuantity(selectedAvailability?.storeKey) // replace with the function that retrieves the cart quantity
			const availableQuantity = getVariantAvailability(
				selectedAvailability?.storeKey,
			)
			if (cartQuantity >= availableQuantity) {
				// handle case when cart quantity is equal to or greater than available quantity

				toast(
					t('bag_quantity_reached_error', {
						cartQuantity: cartQuantity,
						availableQuantity: availableQuantity,
					}),
					{ icon: <Icon name="exclamation" size="lg" /> },
				)
				return
			}
			trackProductAdded(
				selectedSku,
				productData,
				activeStyle,
				firstAvailability,
				location.state?.amplitudePageSource,
				sizeOptions,
				clerkVisitor,
				metaPixelFetcher,
				t,
			)

			productFetcher.submit(
				{
					sku: selectedSku,
					styleNumber: productData?.productInfo.key || '',
					storeKey: loaderData?.storeParams.length
						? (selectedAvailability?.storeKey as string)
						: '',
				},
				{
					method: 'POST',
					action: `/product/${productData?.productInfo
						.key}?${searchParams.toString()}`,
				},
			)
		} else {
			setSelectSizeOpen(true)
		}
	}

	const sizeOptions: SizeOption[] = getSizeOptions(
		activeStyle,
		loaderData,
		t,
		productData.sizeSet,
	)

	const handleAddToBagInstant = (sizeOption: SizeOption) => {
		// sizeOption.value is a sku format: 5715365255280
		// if sizeOption value match the regex of a sku, length and numbers, it means it's a sku
		// if it's not a sku, we should not proceed
		// else if badgeProps is not there, and it's not a sku, we should not proceed
		if (
			sizeOption.badgeProps?.type === 'availableInStore' &&
			!/^\d+$/.test(sizeOption.value)
		) {
			return
		} else if (
			!sizeOption.badgeProps?.type &&
			!/^\d+$/.test(sizeOption.value)
		) {
			return
		}

		// Same checks, but for the given SKU
		const sku = sizeOption.value

		// If size is available in another store we need to select the channel and add to the cart
		if (sizeOption.badgeProps?.type === 'availableInStore') {
			// Find which style variant contains this size/variant
			const targetStyleVariant = sizeOption.variant
				? productData?.styleAvailableVariants.find(style =>
						style.variants.some(v => v.sku === sku),
				  )
				: activeStyle

			// If found in a different style variant, use that one's availability info
			const relevantStyle = targetStyleVariant || activeStyle
			const sizeAvailability =
				relevantStyle.generalAvailability.bySize[sizeOption.label]

			if (sizeAvailability?.storeAvailability?.length > 0) {
				const availableStore = sizeAvailability.storeAvailability?.reduce(
					(acc, item) => {
						return item.quantity > acc.quantity ? item : acc
					},
					sizeAvailability.storeAvailability[0],
				)

				// Add to cart
				productFetcher.submit(
					{
						sku: sku,
						styleNumber: productData?.productInfo.key || '',
						storeKey: availableStore.storeKey,
					},
					{
						method: 'POST',
						action: `/product/${productData?.productInfo
							.key}?${searchParams.toString()}`,
					},
				)
			}
		} else {
			const selectedAvailability = activeStyle.variants
				.find(v => v.sku === sku)
				?.availability?.find(
					it =>
						(it.availableQuantity as number) > 0 &&
						loaderData?.storeParams.includes(it.storeKey as string),
				)

			const cartQuantity = (() => {
				if (!activeStyle || !cart) return 0
				const selectedVariant = activeStyle.variants.find(v => v.sku === sku)
				if (!selectedVariant) return 0

				const channelId = selectedVariant.availability?.find(
					it => it.storeKey === selectedAvailability?.storeKey,
				)?.channelKey

				return cart.lineItems.reduce((acc: number, item: LineItem) => {
					const isVariant = item.variant.sku === sku
					const isChannel = selectedAvailability?.storeKey
						? item.distributionChannel?.id === channelId
						: true
					return isVariant && isChannel ? acc + item.quantity : acc
				}, 0)
			})()

			const availableQuantity = (() => {
				if (!activeStyle) return 0
				const selectedVariant = activeStyle.variants.find(v => v.sku === sku)
				if (!selectedVariant) return 0

				if (selectedAvailability?.storeKey) {
					const av = selectedVariant.availability?.find(
						a => a.storeKey === selectedAvailability.storeKey,
					)
					return av?.availableQuantity || 0
				}

				return (
					selectedVariant.availability?.reduce((sum, av) => {
						return sum + (av.availableQuantity || 0)
					}, 0) || 0
				)
			})()

			if (cartQuantity >= availableQuantity) {
				toast(
					t('bag_quantity_reached_error', {
						cartQuantity,
						availableQuantity,
					}),
					{ icon: <Icon name="exclamation" size="lg" /> },
				)
				return
			}

			// Fire analytics
			trackProductAdded(
				sku,
				productData,
				activeStyle,
				firstAvailability,
				amplitudePageSource,
				sizeOptions,
				clerkVisitor,
				metaPixelFetcher,
				t,
			)

			// Submit
			productFetcher.submit(
				{
					sku,
					styleNumber: productData?.productInfo.key || '',
					storeKey: loaderData?.storeParams.length
						? (selectedAvailability?.storeKey as string)
						: '',
				},
				{
					method: 'POST',
					action: `/product/${productData?.productInfo
						.key}?${searchParams.toString()}`,
				},
			)
		}
	}

	const handleSizeSelection = (value: string) => {
		setSelectedSku(value)
		const sizeOption = sizeOptions.find(option => option.value === value)
		if (!sizeOption) return

		// If size is available in another store
		if (sizeOption.badgeProps?.type === 'availableInStore') {
			// Use the original size label which correctly handles "ONE SIZE"
			const sizeAvailability =
				activeStyle.generalAvailability.bySize[sizeOption.label]

			if (sizeAvailability?.storeAvailability.length > 0) {
				const availableStore = sizeAvailability.storeAvailability.reduce(
					(prev, current) =>
						prev.quantity > current.quantity ? prev : current,
				)
				// Update store param and select the SKU
				const newParams = new URLSearchParams(searchParams)
				newParams.set('store', availableStore.storeKey)
				newParams.set('sku', sizeAvailability.sku)
				setSearchParams(newParams)
			}
		}
	}

	const defaultSize = sizeOptions.find(option => option.value === selectedSku)

	const navigation = useNavigation()
	const isNavigating = navigation.state !== 'idle'

	// If productData is not available yet (e.g., during initial load or navigation),
	// you might want to render a loading state or return null.
	if (!loaderData?.productData) {
		return <LoadingSpinner /> // Or return null, or some other placeholder
	}

	return (
		<>
			<div className="flex flex-col gap-2 md:hidden">
				<div>
					<Carousel
						plugins={[WheelGesturesPlugin()]}
						opts={{
							dragFree: true,
							align: 'start',
						}}
						className="w-full"
					>
						<CarouselContent className="ml-0 gap-2">
							{productData?.styleAvailableVariants.map(
								(styleVariant, index) => {
									return (
										<CarouselItem
											key={styleVariant.lowestPrice.id}
											className="min-w-[60px] basis-[60px] pl-0"
										>
											<Link
												reloadDocument={false}
												onClick={() => {
													setSelectedSku('')
												}}
												className={cn(
													'relative',
													!styleVariant.isAvailableInSelectedStore
														? 'relative'
														: '',
												)}
												to={{
													search: `?style=${
														styleVariant.variants[0].styleVariantKey
													}${
														loaderData?.storeParams.length
															? `&store=${loaderData?.storeParams.join(',')}`
															: ''
													}`,
												}}
												state={location.state ? location.state : null}
											>
												<div className="flex h-full flex-col items-center gap-1">
													<div className="relative h-full">
														{styleVariant.variants.length &&
															styleVariant.variants[0].images && (
																<ImageComponent
																	size="thumbnail"
																	className={`h-full w-[60px] rounded-[2px] object-cover ${
																		styleVariant.active
																			? 'border-[1px] border-black-90'
																			: 'border-[1px] border-white'
																	}  `}
																	src={
																		styleVariant.variants.length &&
																		styleVariant.variants[0].images
																			? styleVariant.variants[0].images[0].url
																			: ''
																	}
																	alt={`Style ${index}`}
																/>
															)}
														{!styleVariant.isAvailableInSelectedStore &&
															!styleVariant.isAvailableInOtherStores && (
																<div
																	className="absolute inset-0 w-full bg-white"
																	style={{ opacity: 0.6 }}
																></div>
															)}
													</div>
												</div>
												{styleVariant.isAvailableInOtherStores &&
													!styleVariant.isAvailableInSelectedStore &&
													loaderData?.storeParams?.length > 0 && (
														<div className="absolute bottom-0 left-0 right-0 min-w-[60px] bg-white px-1 py-1 text-center opacity-80">
															<PlentyBody
																size="xxs"
																className="leading-tight text-black-90"
															>
																{t('other_store')}
															</PlentyBody>
														</div>
													)}
											</Link>
										</CarouselItem>
									)
								},
							)}
						</CarouselContent>
					</Carousel>
				</div>
			</div>

			<div className="fixed bottom-0 left-0 right-0 z-40 w-full border-t border-t-beige-50 bg-white px-4 pb-6 pt-2 md:hidden">
				<NewSizeSelector
					sizeOptions={sizeOptions}
					brandKey={brandKey}
					completelySoldOut={completelySoldOut}
					onAddToBag={sku => handleAddToBagInstant(sku)}
				/>
			</div>

			<div className="hidden md:flex md:flex-col">
				{/* Component for Size Selection */}
				<div className="mb-4 flex w-full flex-col space-y-2">
					<div className="flex justify-end ">
						<SizeGuideDialog brandKey={brandKey} />
					</div>

					<Select
						key={activeStyle.color}
						onValueChange={handleSizeSelection}
						value={defaultSize ? defaultSize.value : undefined}
						open={selectSizeOpen}
						onOpenChange={open => setSelectSizeOpen(open)}
						disabled={completelySoldOut}
					>
						<SelectTrigger className="hover:shadow-sm">
							<SelectValue
								placeholder={
									soldOut ? t('out_of_stock') : t('select_size_label')
								}
							/>
							{/* Chevron icon is handled within SelectTrigger */}
						</SelectTrigger>
						<SelectContent className="z-50" id="pdp-select-size">
							{sizeOptions.map((option: any) => (
								<SelectItem
									disabled={option.disabled}
									key={option.value}
									value={option.value}
									badgeType={option.badgeProps?.type}
									badgeText={option.badgeProps?.text}
								>
									{option.label}
								</SelectItem>
							))}
						</SelectContent>
					</Select>
				</div>

				<div className="flex">
					<Button
						type="submit"
						variant="primary"
						size="primary"
						onClick={handleAddToBag}
						disabled={
							productFetcher.state !== 'idle' ||
							completelySoldOut ||
							isNavigating
						}
						className="w-full rounded-lg text-lg"
						fullWidth
						data-testid="pdp-add-to-bag-button"
					>
						<LoadingSpinner
							className={cn(
								'mr-2',
								productFetcher.state === 'idle' && !isNavigating && 'hidden',
							)}
						/>
						{t('add_to_shopping_bag')}
					</Button>
				</div>
			</div>
		</>
	)
}

export default ProductActions

export const getBadgeForSelectItem = (
	discounted: DiscountedPrice | undefined,
	outOfStock: boolean,
	fewStock: number,
	translationInstance: TFunction<'product_details_page', undefined>,
) => {
	// if (outOfStock) return { type: 'outOfStock', text: 'notify me' }
	if (discounted)
		return {
			type: 'sale',
			text: translationInstance('now_for_sale', {
				saleValue: moneyFormatWithTypedMoney(discounted.value),
			}),
		}
	else if (fewStock <= 2 && fewStock > 0)
		return {
			type: 'fewStock',
			text: translationInstance('only_left', { count: fewStock }),
		}
	return undefined
}
